import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ClipboardList, ListChecks, PawPrint, Trash2, Users, UsersRound } from "lucide-react";
import { Avatar, Button, Dialog, Field, FieldLabel, Input, Select, Textarea } from "@akuna/ui";
import { Autocomplete } from "@/components/ui/autocomplete";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import { useToast } from "@/hooks/use-toast";
import { useVeterinarians } from "@/hooks/useAppointments";
import { useStayPacks } from "@/hooks/useStayPacks";
import { usePatientSearchForQuickStart, type PatientSearchOption } from "@/hooks/usePatientSearchForQuickStart";
import { useDaycareHotelPetsByIds } from "@/components/pets/daycare-hotel/usePetDaycareHotelDraft";
import { useStayActivityTypes } from "./useStayActivityTypes";
import { useCreateStayActivity, useDeleteStayActivity, useUpdateStayActivity } from "./useActivityMutations";
import { ActivityRecurrenceField } from "./ActivityRecurrenceField";
import type { ActivityRecurrence, ActivityTargetType, StayActivityRecord } from "./activitiesModel";
import { PatientSearchResult } from "../PatientSearchResult";

const TARGET_ICONS = { pet: PawPrint, pack: Users, all_packs: UsersRound, general: ClipboardList } as const;

export default function NewActivityDialog({
  open, onOpenChange, defaultDate, activity,
}: { open: boolean; onOpenChange: (v: boolean) => void; defaultDate: string; activity?: StayActivityRecord }) {
  const isEdit = !!activity;
  const { t } = useTranslation("daycareHotel");
  const { toast } = useToast();
  const types = useStayActivityTypes();
  const { data: professionals = [] } = useVeterinarians();
  const { data: packs = [] } = useStayPacks();
  const create = useCreateStayActivity();
  const update = useUpdateStayActivity();
  const remove = useDeleteStayActivity();

  const [targetType, setTargetType] = useState<ActivityTargetType>(activity?.targetType ?? "pet");
  const [pet, setPet] = useState<PatientSearchOption | null>(null);
  const [packId, setPackId] = useState<string>(activity?.targetPackId ?? "");
  const [typeId, setTypeId] = useState<string>(activity?.typeId ?? "");
  const [time, setTime] = useState<string>(activity?.scheduledTime ? activity.scheduledTime.slice(0, 5) : "15:00");
  const [description, setDescription] = useState<string>(activity?.description ?? "");
  const [assignee, setAssignee] = useState<string | null>(activity?.assignedTo ?? null);
  const [recurrence, setRecurrence] = useState<ActivityRecurrence | null>(activity?.recurrence ?? null);
  const [petSearch, setPetSearch] = useState("");
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const { data: petOptions = [], isFetching } = usePatientSearchForQuickStart(petSearch);
  const petByValue = useMemo(() => new Map(petOptions.map((o) => [o.value, o])), [petOptions]);
  const linkedPetId = activity?.targetType === "pet" ? activity.targetPetId : null;
  const { data: linkedPets = {} } = useDaycareHotelPetsByIds(linkedPetId ? [linkedPetId] : []);
  const linkedPet = linkedPetId ? linkedPets[linkedPetId] : null;
  const {
    value: linkedValue,
    label: linkedLabel,
    ownerName: linkedOwnerName,
    petId: linkedId,
    petName: linkedName,
    petRecordNumber: linkedRecordNumber,
    petPhotoPath: linkedPhotoPath,
  } = linkedPet ?? {};

  useEffect(() => {
    if (!open || !linkedId) return;
    setPet({
      value: linkedValue,
      label: linkedLabel,
      ownerId: "",
      ownerName: linkedOwnerName,
      ownerDocument: null,
      ownerPrimaryPhone: null,
      ownerPrimaryEmail: null,
      ownerPrimaryAddress: null,
      petId: linkedId,
      petName: linkedName,
      petRecordNumber: linkedRecordNumber,
      petPhotoPath: linkedPhotoPath,
      petSpeciesName: null,
      petBreedName: null,
      petStatusName: null,
      petDeathDate: null,
    });
  }, [
    linkedLabel,
    linkedOwnerName,
    linkedId,
    linkedName,
    linkedPhotoPath,
    linkedRecordNumber,
    linkedValue,
    open,
  ]);

  const effectiveTypeId = typeId || types.data?.[0]?.id || "";
  const targets: ActivityTargetType[] = ["pet", "pack", "all_packs", "general"];

  const canSubmit =
    !!effectiveTypeId &&
    (targetType !== "pet" || !!pet) &&
    (targetType !== "pack" || !!packId);

  const reset = () => {
    setTargetType("pet"); setPet(null); setPackId(""); setTypeId(""); setTime("15:00");
    setDescription(""); setAssignee(null); setRecurrence(null); setPetSearch("");
  };

  const submit = async () => {
    if (!canSubmit) return;
    try {
      if (isEdit && activity) {
        await update.mutateAsync({
          activityId: activity.id,
          typeId: effectiveTypeId,
          scheduledTime: time || null,
          description: description.trim() || null,
          assignedTo: assignee,
          recurrence,
        });
        toast({ title: t("activities.updated") });
      } else {
        await create.mutateAsync({
          targetType,
          targetPetId: targetType === "pet" ? pet?.petId ?? null : null,
          targetPackId: targetType === "pack" ? packId : null,
          typeId: effectiveTypeId,
          scheduledTime: time || null,
          description: description.trim() || null,
          assignedTo: assignee,
          scheduledDate: defaultDate,
          recurrence,
        });
        toast({ title: t("activities.created") });
      }
      reset();
      onOpenChange(false);
    } catch {
      toast({ title: isEdit ? t("activities.updateError") : t("activities.createError"), variant: "destructive" });
    }
  };

  const handleDelete = async () => {
    if (!activity || remove.isPending) return;
    try {
      await remove.mutateAsync(activity.id);
      toast({ title: t("activities.deleted") });
      setDeleteConfirmationOpen(false);
      onOpenChange(false);
    } catch {
      toast({ title: t("activities.deleteError"), variant: "destructive" });
    }
  };

  return (
    <>
    <Dialog
      open={open}
      onOpenChange={(next) => { if (!next) { reset(); setDeleteConfirmationOpen(false); } onOpenChange(next); }}
      mode="adaptive"
      title={isEdit ? t("activities.editActivity") : t("activities.newActivity")}
      header={<span>{isEdit ? t("activities.editActivitySubtitle") : t("activities.newActivitySubtitle")}</span>}
      closeLabel={t("close")}
      icon={
        <span className="flex h-11 w-11 items-center justify-center rounded-xl" style={{ background: "#dde6fa", color: "#2c3f7f" }}>
          <ListChecks aria-hidden size={22} />
        </span>
      }
      footer={
        <div className="flex w-full items-center justify-between">
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("cancel")}</Button>
          <Button disabled={!canSubmit || create.isPending || update.isPending || remove.isPending} loading={create.isPending || update.isPending} onClick={submit}>{isEdit ? t("save") : t("activities.create")}</Button>
        </div>
      }
    >
      <div className="grid gap-5">
        {/* Para quem */}
        <Field>
          <FieldLabel>{t("activities.targetLabel")}</FieldLabel>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {targets.map((target) => {
              const Icon = TARGET_ICONS[target];
              const active = targetType === target;
              const label = target === "pet" ? t("activities.targetPet")
                : target === "pack" ? t("activities.targetPack")
                : target === "all_packs" ? t("activities.targetAllPacks")
                : t("activities.targetGeneral");
              return (
                <button
                  key={target}
                  type="button"
                  disabled={isEdit}
                  onClick={() => setTargetType(target)}
                  className={`flex items-center gap-2.5 rounded-xl border px-3.5 py-3 text-left text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                    active ? "border-primary bg-primary/10 font-semibold text-foreground" : "border-border text-muted-foreground"
                  }`}
                >
                  <Icon size={18} className={active ? "text-primary" : "text-muted-foreground"} />
                  {label}
                </button>
              );
            })}
          </div>
        </Field>

        {targetType === "pet" && (
          <Field>
            <FieldLabel>{t("petAndOwner")}</FieldLabel>
            {pet ? (
              <div className="flex items-center gap-3 rounded-xl border border-border bg-card p-3">
                <PatientSearchResult option={pet} variant="selected" onRemove={() => { setPet(null); setPetSearch(""); }} />
              </div>
            ) : (
              <Autocomplete
                value={petSearch}
                options={petOptions.map((o) => ({ value: o.value, label: o.label }))}
                onChange={setPetSearch}
                onSelect={(o) => { setPet(petByValue.get(o.value) ?? null); setPetSearch(""); }}
                disableLocalFilter
                popoverPortalled={false}
                placeholder={t("searchPetOwner")}
                emptyMessage={isFetching ? t("loading") : t("noPetsFound")}
                renderOption={(option) => {
                  const selected = petByValue.get(option.value);
                  return selected ? <PatientSearchResult option={selected} variant="option" /> : <span>{option.label}</span>;
                }}
              />
            )}
          </Field>
        )}

        {targetType === "pack" && (
          <Select
            label={t("activities.targetPack")}
            value={packId}
            onValueChange={setPackId}
            placeholder={t("activities.selectPack")}
            options={packs.map((p) => ({ value: p.id, label: p.name }))}
          />
        )}

        {/* Tipo + Horário */}
        <div className="grid gap-3 sm:grid-cols-[1.4fr_1fr]">
          <Select
            label={t("activities.typeLabel")}
            value={effectiveTypeId}
            onValueChange={setTypeId}
            options={(types.data ?? []).map((tp) => ({ value: tp.id, label: tp.name }))}
          />
          <Field>
            <FieldLabel>{t("activities.timeLabel")}</FieldLabel>
            <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} />
          </Field>
        </div>

        {/* Recorrência */}
        <ActivityRecurrenceField value={recurrence} scheduledDate={defaultDate} onChange={setRecurrence} />

        {/* Descrição */}
        <Field>
          <FieldLabel>{t("activities.descriptionLabel")}</FieldLabel>
          <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder={t("activities.descriptionPlaceholder")} rows={2} />
        </Field>

        {/* Responsável */}
        <Field>
          <FieldLabel>{t("activities.assigneeLabel")}</FieldLabel>
          <div className="flex flex-wrap gap-2">
            {professionals.map((pro) => {
              const active = assignee === pro.id;
              return (
                <button
                  key={pro.id}
                  type="button"
                  onClick={() => setAssignee(active ? null : pro.id)}
                  className={`inline-flex items-center gap-2 rounded-full border py-1.5 pl-1.5 pr-3.5 text-[13px] transition-colors ${
                    active ? "border-primary bg-primary/10 font-semibold text-primary" : "border-border text-muted-foreground"
                  }`}
                >
                  <Avatar className="h-6 w-6 text-[10px]" name={pro.full_name} fallback={pro.full_name.slice(0, 1)} />
                  {pro.full_name}
                </button>
              );
            })}
          </div>
        </Field>

        {isEdit && (
          <Button
            className="w-full"
            variant="destructive"
            disabled={remove.isPending}
            onClick={() => setDeleteConfirmationOpen(true)}
          >
            <Trash2 aria-hidden="true" />
            {t("activities.delete")}
          </Button>
        )}

      </div>
    </Dialog>
    <ConfirmationDialog
      open={deleteConfirmationOpen}
      onOpenChange={setDeleteConfirmationOpen}
      title={t("activities.deleteTitle")}
      description={t("activities.deleteDescription")}
      confirmText={t("activities.deleteConfirm")}
      cancelText={t("cancel")}
      variant="destructive"
      onConfirm={() => { void handleDelete(); }}
    />
    </>
  );
}
